import React, { useState, useEffect } from "react";
import { useForm, useFormContext, useFieldArray, FormProvider } from "react-hook-form";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
  DraggableProvidedDragHandleProps,
} from "react-beautiful-dnd";
import { MdDragIndicator } from "react-icons/md";
import { coursegroupsApi, CourseGroupApi, ProgramModuleApi } from "../../api/coursegroups/coursegroupsApi";
import { useCourseGroupCourses } from "../../api/coursegroups/useCourseGroupCourses";
import { PaziaFromQuill } from "../../components/atoms/PaziaQuill/PaziaFromQuill";
import { PaziaSelect } from "../../components/atoms/PaziaSelect/PaziaSelect";
import { COURSE_STATUS, COURSE_STATUS_NAMES } from "../../consts/courseType";

interface PrepopulateFormValues {
  sourceCourseId: string;
}

const STATUSES_ELIGIBLE_FOR_DEFAULT: number[] = [COURSE_STATUS.PLANNED, COURSE_STATUS.READY, COURSE_STATUS.ONGOING];

interface ModuleFormValues {
  name: string;
  icon: string | null;
  content: string;
  tags: string[];
}

interface ProgramFormValues {
  modules: ModuleFormValues[];
}

interface ProgramEditorProps {
  courseGroup: CourseGroupApi;
}

export const ProgramEditor = ({ courseGroup }: ProgramEditorProps) => {
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrepopulating, setIsPrepopulating] = useState(false);
  const [showPrepopulateConfirm, setShowPrepopulateConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingAutoExpandRef = React.useRef<"first" | "last" | null>(null);

  const { courses } = useCourseGroupCourses(courseGroup.id.toString());

  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: `${course.name} (edycja ${course.edition}${course.variant ? ` ${course.variant}` : ""}) - ${
      (COURSE_STATUS_NAMES as Record<number, string>)[course.status] ?? course.status
    }`,
  }));

  const defaultSourceCourseId = courses
    .filter((course) => STATUSES_ELIGIBLE_FOR_DEFAULT.includes(course.status))
    .sort((a, b) => b.id - a.id)[0]?.id.toString();

  const prepopulateForm = useForm<PrepopulateFormValues>({
    defaultValues: { sourceCourseId: "" },
  });

  const form = useForm<ProgramFormValues>({
    defaultValues: { modules: [] },
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "modules",
  });

  useEffect(() => {
    if (!pendingAutoExpandRef.current || fields.length === 0) return;
    if (pendingAutoExpandRef.current === "first") {
      setExpandedModules(new Set([fields[0].id]));
    } else {
      setExpandedModules((prev) => new Set([...prev, fields[fields.length - 1].id]));
    }
    pendingAutoExpandRef.current = null;
  }, [fields]);

  const mapApiToForm = (modules: ProgramModuleApi[]): ModuleFormValues[] =>
    modules.map((m) => ({
      name: m.name,
      icon: m.icon,
      content: m.content ?? "",
      tags: m.tags,
    }));

  const loadProgram = async () => {
    try {
      const response = await coursegroupsApi.getProgram(courseGroup.id);
      form.reset({ modules: mapApiToForm(response.data) });
      setExpandedModules(new Set());
      pendingAutoExpandRef.current = "first";
    } catch (err) {
      console.error("Error loading program:", err);
      setError("Nie udało się załadować programu.");
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadProgram();
      setIsLoading(false);
    };
    init();
  }, [courseGroup.id]);

  const openPrepopulateConfirm = () => {
    prepopulateForm.reset({ sourceCourseId: defaultSourceCourseId ?? "" });
    setShowPrepopulateConfirm(true);
  };

  const handlePrepopulate = async () => {
    const { sourceCourseId } = prepopulateForm.getValues();
    if (!sourceCourseId) return;
    setShowPrepopulateConfirm(false);
    setIsPrepopulating(true);
    setError(null);
    try {
      await coursegroupsApi.prepopulateProgram(courseGroup.id, Number(sourceCourseId));
      await loadProgram();
    } catch (err) {
      setError("Nie udało się wygenerować programu z harmonogramów.");
      console.error("Error prepopulating program:", err);
    } finally {
      setIsPrepopulating(false);
    }
  };

  const toggleModule = (id: string) => {
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const values = form.getValues();
      const program: ProgramModuleApi[] = values.modules.map((m) => ({
        name: m.name,
        icon: m.icon,
        content: m.content || null,
        tags: m.tags,
      }));
      await coursegroupsApi.updateProgram(courseGroup.id, program);
      form.reset(values);
    } catch (err) {
      setError("Nie udało się zapisać programu. Spróbuj ponownie.");
      console.error("Error saving program:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const addModule = () => {
    append({ name: "", icon: null, content: "", tags: [] });
    pendingAutoExpandRef.current = "last";
  };

  const removeModule = (index: number, id: string) => {
    remove(index);
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    move(result.source.index, result.destination.index);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <div className="border-primary-500 h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"></div>
          <span>Ładowanie programu...</span>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {form.formState.isDirty && !isSaving && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <svg className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Masz niezapisane zmiany. Pamiętaj, aby zapisać program przed opuszczeniem strony.
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">Moduły programu</h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openPrepopulateConfirm}
              disabled={isPrepopulating}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              {isPrepopulating ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent"></div>
                  Generuję...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Wygeneruj z harmonogramów
                </>
              )}
            </button>
            <button
              type="button"
              onClick={addModule}
              className="bg-primary-500 hover:bg-primary-600 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Dodaj moduł
            </button>
          </div>
        </div>

        {/* Prepopulate Confirmation Modal */}
        {showPrepopulateConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
              <h4 className="mb-2 text-lg font-medium text-slate-800 dark:text-slate-100">
                Wygenerować program z harmonogramów?
              </h4>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                Wybierz edycję kursu, z której harmonogramu ma zostać wygenerowany program. Istniejące dane zostaną
                nadpisane.
              </p>
              <FormProvider {...prepopulateForm}>
                <div className="mb-4">
                  <PaziaSelect
                    name="sourceCourseId"
                    label="Edycja kursu"
                    required
                    options={courseOptions}
                    placeholder="Wybierz edycję..."
                  />
                </div>
              </FormProvider>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowPrepopulateConfirm(false)}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={handlePrepopulate}
                  disabled={!prepopulateForm.watch("sourceCourseId")}
                  className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 rounded-md px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed"
                >
                  Wygeneruj
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modules List */}
        {fields.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center dark:border-slate-600 dark:bg-slate-700/50">
            <svg
              className="mx-auto mb-2 h-8 w-8 text-slate-400 dark:text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-sm text-slate-500 dark:text-slate-400">Nie dodano jeszcze żadnych modułów</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">Kliknij "Dodaj moduł" aby rozpocząć</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="modules">
              {(provided: DroppableProvided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                  {fields.map((field, index) => (
                    <Draggable key={field.id} draggableId={field.id} index={index}>
                      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={snapshot.isDragging ? "opacity-50" : ""}
                        >
                          <ModuleCard
                            index={index}
                            isExpanded={expandedModules.has(field.id)}
                            onToggle={() => toggleModule(field.id)}
                            onRemove={() => removeModule(index, field.id)}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Save Button */}
        {fields.length > 0 && (
          <div className="flex justify-end border-t border-slate-200 pt-4 dark:border-slate-700">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !form.formState.isDirty}
              className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Zapisuję...
                </>
              ) : (
                "Zapisz zmiany"
              )}
            </button>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

interface ModuleCardProps {
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

const ModuleCard = ({ index, isExpanded, onToggle, onRemove, dragHandleProps }: ModuleCardProps) => {
  const { register, watch, setValue, getValues } = useFormContext<ProgramFormValues>();
  const [tagInput, setTagInput] = useState("");

  const moduleName = watch(`modules.${index}.name`);
  const tags = watch(`modules.${index}.tags`);

  const handleAddTag = () => {
    if (!tagInput.trim()) return;
    const currentTags = getValues(`modules.${index}.tags`);
    if (currentTags.includes(tagInput.trim())) return;
    setValue(`modules.${index}.tags`, [...currentTags, tagInput.trim()], { shouldDirty: true });
    setTagInput("");
  };

  const handleRemoveTag = (tagIndex: number) => {
    const currentTags = getValues(`modules.${index}.tags`);
    setValue(
      `modules.${index}.tags`,
      currentTags.filter((_: string, i: number) => i !== tagIndex),
      { shouldDirty: true },
    );
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      {/* Module Header */}
      <div
        className="flex cursor-pointer items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div
            {...dragHandleProps}
            onClick={(e) => e.stopPropagation()}
            className="cursor-grab text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            title="Przeciągnij, aby zmienić kolejność"
          >
            <MdDragIndicator size={20} />
          </div>
          <svg
            className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-medium text-slate-800 dark:text-slate-100">
            {moduleName || `Moduł ${index + 1}`}
          </span>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
          title="Usuń moduł"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* Module Content */}
      {isExpanded && (
        <div className="border-t border-slate-200 p-4 dark:border-slate-700">
          <div className="space-y-4">
            {/* Module Name */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Nazwa modułu
              </label>
              <input
                type="text"
                {...register(`modules.${index}.name`)}
                placeholder="Wprowadź nazwę modułu..."
                className="form-input h-[2.375rem] w-full dark:bg-slate-700"
              />
            </div>

            {/* WYSIWYG Content */}
            <PaziaFromQuill name={`modules.${index}.content`} label="Treść modułu" />

            {/* Tags Section */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Tagi</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Dodaj tag..."
                  className="form-input h-[2.375rem] flex-1 dark:bg-slate-700"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim()}
                  className="bg-slate-100 hover:bg-slate-200 disabled:bg-slate-50 rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors disabled:cursor-not-allowed disabled:text-slate-400 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                >
                  Dodaj
                </button>
              </div>
              {tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.map((tag: string, tagIndex: number) => (
                    <span
                      key={tagIndex}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-sm text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tagIndex)}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
